import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Filter from "../../../Filter";
import DateRangeFilterInput from "../../../Filter/DateRangeFilterInput";
import WorkingHoursFilterInput from "../../../Filter/WorkingHoursFilterInput";
import { UICountryFlag } from "../../../ui";
import { genders } from "../../../../utils/Constants";
import { joiningRequestStatuses } from "./constants";

const ApplicantsFilters = ({
  activeStatus,
  applicantsTab,
  tableLabels,
  copy,
  nationalityOptions,
  roleOptions,
  shiftOptions,
  zoneOptions,
  ratingOptions,
  cityOptions,
  search,
  nationalitySearch,
  nationalityLoading,
  filters,
  onSearchChange,
  onNationalitySearchChange,
  onNationalityOpen,
  onFilterChange,
  renderActionBeforeFilterToggle,
  showApplicantTypeFilter = true,
  showCheckInFilter = false,
}) => {
  const nationalityFilterOptions = useMemo(() => {
    const options = nationalityOptions.map((option) => ({
        ...option,
        icon: (option.flag || option.shortCode) && (
          <UICountryFlag flag={option.flag} shortCode={option.shortCode} />
        ),
      }));
    const selectedNationality = filters.nationality;

    if (
      selectedNationality?.id &&
      !options.some(
        (option) => String(option.id) === String(selectedNationality.id),
      )
    ) {
      options.unshift({
        ...selectedNationality,
        icon: (
          <UICountryFlag
            flag={selectedNationality.flag}
            shortCode={selectedNationality.shortCode}
          />
        ),
      });
    }

    return options;
  }, [filters.nationality, nationalityOptions]);

  const [roleInput, setRoleInput] = useState(
    () => filters.requestedRole?.name || "",
  );

  useEffect(() => {
    setRoleInput(filters.requestedRole?.name || "");
  }, [filters.requestedRole]);

  const roleFilterOptions = useMemo(() => {
    const options = [...roleOptions];
    const selectedRole = filters.requestedRole;

    if (
      selectedRole?.id &&
      !options.some(
        (option) => String(option.id) === String(selectedRole.id),
      )
    ) {
      options.unshift({
        id: selectedRole.id,
        name: selectedRole.name || String(selectedRole.id),
      });
    }

    return options;
  }, [filters.requestedRole, roleOptions]);

  const resolvedRequestedRole = useMemo(() => {
    if (!filters.requestedRole?.id) return null;

    return (
      roleFilterOptions.find(
        (option) => String(option.id) === String(filters.requestedRole.id),
      ) ?? filters.requestedRole
    );
  }, [filters.requestedRole, roleFilterOptions]);

  const filterOptions = useMemo(
    () => ({
      nationalities: nationalityFilterOptions,
      roles: roleFilterOptions,
      shifts: shiftOptions,
      zones: zoneOptions,
      ratings: ratingOptions,
      cities: cityOptions,
      genders: [
        { id: 1, value: genders.male, name: applicantsTab.genderOptions?.male },
        { id: 2, value: genders.female, name: applicantsTab.genderOptions?.female },
      ],
      checkIn: [
        { id: 1, value: "checked_in", name: tableLabels.checkedIn },
        { id: 2, value: "not_checked_in", name: tableLabels.nonCheckedIn },
      ],
      applicantTypes: [
        { id: 1, value: "loyal", name: copy.applicantTypeFilter?.options?.loyal },
        { id: 2, value: "other", name: copy.applicantTypeFilter?.options?.other },
      ],
      allocationStatuses: [
        {
          id: 1,
          value: "assigned",
          name: copy.allocationStatusFilter?.options?.assigned,
        },
        {
          id: 2,
          value: "not_assigned",
          name: copy.allocationStatusFilter?.options?.notAssigned,
        },
      ],
    }),
    [
      applicantsTab.genderOptions,
      copy.allocationStatusFilter,
      copy.applicantTypeFilter,
      cityOptions,
      nationalityFilterOptions,
      ratingOptions,
      roleFilterOptions,
      shiftOptions,
      tableLabels.checkedIn,
      tableLabels.nonCheckedIn,
      zoneOptions,
    ],
  );
  const roleLabel =
    activeStatus === joiningRequestStatuses.joined
      ? tableLabels.role
      : tableLabels.requestedRole;
  const isJoined = activeStatus === joiningRequestStatuses.joined;
  const isRemoved = activeStatus === joiningRequestStatuses.removed;
  const dateFilterLabel = isRemoved
    ? tableLabels.removedDate
    : tableLabels.requestDate;

  const filterItems = [
    !isJoined && {
      type: "component",
      name: "workingHours",
      render: () => (
        <WorkingHoursFilterInput
          placeholder={copy.workingHoursFilter?.label}
          labels={copy.workingHoursFilter?.options}
          value={filters.workingHours}
          onChange={(value) => onFilterChange("workingHours", value)}
        />
      ),
    },
    !isJoined && {
      type: "component",
      name: "requestDate",
      className: "col-sm-12 col-md-6 col-lg-3",
      render: () => (
        <DateRangeFilterInput
          name="requestDate"
          placeholder={dateFilterLabel}
          labels={copy.requestDateFilter}
          value={filters.requestDate}
          onChange={(value) => onFilterChange("requestDate", value)}
        />
      ),
    },
    {
      type: "filter",
      name: "nationality",
      placeholder: applicantsTab.nationality,
      value: filters.nationality,
      options: filterOptions.nationalities,
      isOptionsElt: true,
      loading: nationalityLoading,
      inputValue: nationalitySearch,
      filterOptions: (options) => options,
      onInputChange: (_event, value, reason) => {
        if (reason === "input" || reason === "clear" || reason === "reset") {
          onNationalitySearchChange(value ?? "");
        }
      },
      onOpen: onNationalityOpen,
      onChange: (value) => {
        onNationalitySearchChange(value?.name || "");
        onFilterChange("nationality", value || null);
      },
    },
    {
      type: "filter",
      name: "gender",
      placeholder: applicantsTab.gender,
      value: filters.gender,
      options: filterOptions.genders,
      onChange: (value) => onFilterChange("gender", value),
    },
    {
      type: "filter",
      name: "city",
      placeholder: applicantsTab.city,
      value: filters.city,
      options: filterOptions.cities,
      onChange: (value) => onFilterChange("city", value),
    },
    isJoined && {
      type: "filter",
      name: "allocationStatus",
      placeholder: copy.allocationStatusFilter?.placeholder,
      value: filters.allocationStatus,
      options: filterOptions.allocationStatuses,
      onChange: (value) => onFilterChange("allocationStatus", value),
    },
    isJoined && {
      type: "filter",
      name: "zone",
      placeholder: applicantsTab.zone,
      value: filters.zone,
      options: filterOptions.zones,
      onChange: (value) => onFilterChange("zone", value),
    },
    {
      type: "filter",
      name: "requestedRole",
      placeholder: roleLabel,
      value: resolvedRequestedRole,
      options: filterOptions.roles,
      inputValue: roleInput,
      onInputChange: (_event, value, reason) => {
        if (reason === "input" || reason === "clear" || reason === "reset") {
          setRoleInput(value ?? "");
        }
      },
      onChange: (value) => {
        setRoleInput(value?.name || "");
        onFilterChange("requestedRole", value || null);
      },
    },
    !isRemoved && {
      type: "filter",
      name: "shift",
      placeholder: applicantsTab.shift,
      value: filters.shift,
      options: filterOptions.shifts,
      onChange: (value) => onFilterChange("shift", value),
    },
    isJoined && showCheckInFilter && {
      type: "filter",
      name: "checkIn",
      placeholder: tableLabels.checkIn,
      value: filters.checkIn,
      options: filterOptions.checkIn,
      onChange: (value) => onFilterChange("checkIn", value),
    },
    {
      type: "filter",
      name: "rating",
      placeholder: tableLabels.rating,
      value: filters.rating,
      options: filterOptions.ratings,
      onChange: (value) => onFilterChange("rating", value),
    },
    showApplicantTypeFilter && {
      type: "filter",
      name: "applicantType",
      placeholder: copy.applicantTypeFilter?.placeholder,
      value: filters.applicantType,
      options: filterOptions.applicantTypes,
      onChange: (value) => onFilterChange("applicantType", value),
    },
  ].filter(Boolean);

  return (
    <Filter
      classNames={{
        base: "",
        search: "mb-3 mb-md-0",
        filter: "",
        container: "",
      }}
      searchInput={{
        placeholder: copy.searchPlaceholder,
        name: "search",
        value: search,
        onChange: onSearchChange,
      }}
      renderActionBeforeFilterToggle={renderActionBeforeFilterToggle}
      filters={filterItems}
    />
  );
};

ApplicantsFilters.propTypes = {
  activeStatus: PropTypes.string.isRequired,
  applicantsTab: PropTypes.shape({
    nationality: PropTypes.string.isRequired,
    shift: PropTypes.string.isRequired,
    zone: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    genderOptions: PropTypes.shape({
      male: PropTypes.string,
      female: PropTypes.string,
    }),
  }).isRequired,
  tableLabels: PropTypes.shape({
    role: PropTypes.string.isRequired,
    requestedRole: PropTypes.string.isRequired,
    requestDate: PropTypes.string.isRequired,
    removedDate: PropTypes.string,
    checkIn: PropTypes.string.isRequired,
    checkedIn: PropTypes.string.isRequired,
    nonCheckedIn: PropTypes.string.isRequired,
    rating: PropTypes.string.isRequired,
  }).isRequired,
  copy: PropTypes.shape({
    searchPlaceholder: PropTypes.string.isRequired,
    workingHoursFilter: PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.objectOf(PropTypes.string).isRequired,
    }).isRequired,
    requestDateFilter: PropTypes.shape({
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }).isRequired,
    applicantTypeFilter: PropTypes.shape({
      placeholder: PropTypes.string,
      options: PropTypes.shape({
        loyal: PropTypes.string,
        other: PropTypes.string,
      }),
    }),
    allocationStatusFilter: PropTypes.shape({
      placeholder: PropTypes.string,
      options: PropTypes.shape({
        assigned: PropTypes.string,
        notAssigned: PropTypes.string,
      }),
    }),
  }).isRequired,
  nationalityOptions: PropTypes.arrayOf(PropTypes.object),
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  shiftOptions: PropTypes.arrayOf(PropTypes.object),
  zoneOptions: PropTypes.arrayOf(PropTypes.object),
  ratingOptions: PropTypes.arrayOf(PropTypes.object),
  cityOptions: PropTypes.arrayOf(PropTypes.object),
  search: PropTypes.string.isRequired,
  nationalitySearch: PropTypes.string,
  nationalityLoading: PropTypes.bool,
  filters: PropTypes.shape({
    nationality: PropTypes.object,
    requestedRole: PropTypes.object,
    shift: PropTypes.object,
    workingHours: PropTypes.object,
    requestDate: PropTypes.object,
    zone: PropTypes.object,
    rating: PropTypes.object,
    checkIn: PropTypes.object,
    applicantType: PropTypes.object,
    allocationStatus: PropTypes.object,
    gender: PropTypes.object,
    city: PropTypes.object,
  }).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onNationalitySearchChange: PropTypes.func.isRequired,
  onNationalityOpen: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  renderActionBeforeFilterToggle: PropTypes.element,
  showApplicantTypeFilter: PropTypes.bool,
  showCheckInFilter: PropTypes.bool,
};

ApplicantsFilters.defaultProps = {
  nationalityOptions: [],
  roleOptions: [],
  shiftOptions: [],
  zoneOptions: [],
  ratingOptions: [],
  cityOptions: [],
  nationalitySearch: "",
  nationalityLoading: false,
  renderActionBeforeFilterToggle: null,
};

export default ApplicantsFilters;
